// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rediger › Geometri's write loop (frontend-u0o.9): batches shown at once
 * (optimistic) and kept on top of the model until a re-read that includes
 * them has landed; what a refused write means (a stale revision is re-read
 * and replayed once, a running pipeline job waits, anything else is
 * dropped with its reason). Pure: no React, no DOM.
 */

import type { ErrorCode, ModelEditOp } from '../api/types';
import type { GeometriModel } from './geometri';
import { applyOps } from './geometriEdit';

export interface PendingEdit {
  id: number;
  label: string;
  ops: ModelEditOp[];
  /** `sending`: on its way; `saved`: the server took it, the re-read numbered `tick` will hold it. */
  state: 'sending' | 'saved';
  /** The model read that includes it (saved only). */
  tick?: number;
}

/** The model with every pending batch on top, oldest first. */
export function withPending(model: GeometriModel, pending: readonly PendingEdit[]): GeometriModel {
  return pending.reduce((m, p) => applyOps(m, p.ops), model);
}

/** The batches still to show once the read numbered `loadedTick` has landed. */
export function settle(pending: readonly PendingEdit[], loadedTick: number): PendingEdit[] {
  const out = pending.filter((p) => p.state === 'sending' || (p.tick ?? Infinity) > loadedTick);
  return out.length === pending.length ? (pending as PendingEdit[]) : out;
}

export type FailureKind = 'conflict' | 'busy' | 'nothing' | 'forbidden' | 'invalid' | 'error';

/** What a refused write means, from its status and `code` (ruxd-202), the status alone for an older server. */
export function failureKind(status: number | null, code: ErrorCode | undefined): FailureKind {
  if (code === 'model_conflict') return 'conflict';
  if (code === 'case_busy' || code === 'database_busy') return 'busy';
  if (code === 'nothing_to_undo' || code === 'nothing_to_redo') return 'nothing';
  if (status === 403) return 'forbidden';
  if (status === 400 || status === 404 || status === 422) return 'invalid';
  if (status === 503) return 'busy';
  // A 409 without a code is the pre-ruxd-202 shape: a stale revision or a job.
  if (status === 409) return 'conflict';
  return 'error';
}

/** The message a dropped write leaves ("Tykkelse · Væg 6 blev ikke gemt: …"). */
export function failureMessage(kind: FailureKind, label: string, detail?: string): string {
  switch (kind) {
    case 'conflict':
      return `${label} blev ikke gemt: modellen er ændret imens — den er læst igen`;
    case 'busy':
      return `${label} blev ikke gemt: en pipeline-kørsel er i gang — prøv igen, når den er færdig`;
    case 'nothing':
      return label === 'Fortryd' ? 'Intet at fortryde' : 'Intet at gentage';
    case 'forbidden':
      return `${label} kræver redigeringsadgang`;
    case 'invalid':
      return `${label} blev afvist${detail ? `: ${detail}` : ''}`;
    case 'error':
      return `${label} mislykkedes${detail ? `: ${detail}` : ''}`;
  }
}

/** "Gemt · rev 142", "Gemmer…", "Ikke gemt". */
export function saveState(revision: number | null, pending: readonly PendingEdit[]): string {
  if (pending.some((p) => p.state === 'sending')) return 'Gemmer…';
  return revision === null ? '' : `Gemt · rev ${revision}`;
}
